import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertPhotoRoundedIcon from "@mui/icons-material/InsertPhotoRounded";
import {
  LANDING_IMAGE_MAX_BYTES,
  acceptedImageFormatsLabel,
  formatImageSize,
  validateImageFile,
} from "../../../utils/imageUpload";

export default function PendingAssetField({
  label,
  file,
  onChange,
  disabled = false,
  accept = "image/svg+xml,image/png,image/jpeg,image/webp,.svg,.png,.jpg,.jpeg,.webp",
  maxBytes = LANDING_IMAGE_MAX_BYTES,
  helperText = "",
}) {
  const inputRef = React.useRef(null);
  const [preview, setPreview] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!(file instanceof File)) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectFile = (event) => {
    const next = event.target.files?.[0] || null;
    event.target.value = "";
    if (!next) return;

    try {
      validateImageFile(next, { accept, maxBytes, fieldLabel: label });
      setError("");
      onChange?.(next);
    } catch (err) {
      setError(err?.message || "No se pudo usar la imagen seleccionada.");
    }
  };

  const clearFile = () => {
    setError("");
    onChange?.(null);
  };

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 2.5,
        border: "1px solid rgba(25,36,47,.1)",
        bgcolor: "rgba(25,36,47,.018)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.05} alignItems={{ sm: "center" }}>
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              bgcolor: "rgba(105,87,232,.08)",
              color: "#6957E8",
            }}
          >
            {preview ? (
              <Box component="img" src={preview} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <InsertPhotoRoundedIcon />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: ".86rem", fontWeight: 950, color: "#19242F", overflowWrap: "anywhere" }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.55)", overflowWrap: "anywhere" }}>
              {file?.name || helperText || "Sin archivo seleccionado"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.5)" }}>
              {acceptedImageFormatsLabel(accept)} · máximo {formatImageSize(maxBytes)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={.6}
          justifyContent={{ xs: "stretch", sm: "flex-start" }}
          sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
        >
          {file ? (
            <IconButton
              size="small"
              disabled={disabled}
              aria-label={`Quitar ${label}`}
              onClick={clearFile}
              sx={{ width: 44, height: 44, flexShrink: 0 }}
            >
              <DeleteOutlineRoundedIcon />
            </IconButton>
          ) : null}

          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            startIcon={<CloudUploadRoundedIcon />}
            onClick={() => inputRef.current?.click()}
            sx={{
              minHeight: 40,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 850,
              flex: { xs: 1, sm: "0 0 auto" },
              minWidth: 0,
            }}
          >
            {file ? "Cambiar" : "Elegir"}
          </Button>
        </Stack>
      </Stack>
      <input ref={inputRef} type="file" hidden accept={accept} onChange={selectFile} />
      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
    </Box>
  );
}
